/**
 * Shared field validation for referral and signup input.
 *
 * This module is the single gate that both the client form and the API run
 * untrusted input through, so every validator here is defensive about shape:
 * a missing, null, or non-string field is coerced to an empty string before
 * it is checked, so a validator reports a field error instead of throwing.
 *
 * `required` and `maxLength` measure `value.trim()`. Callers must store the
 * trimmed value, not the raw one, so what was validated is what is saved.
 */

export const MAX_PHOTOS = 20;
export const MIN_PASSWORD_LENGTH = 12;

export type FieldErrors = Record<string, string>;

export interface ReferralInput {
  customerName: string;
  customerAddress: string;
  customerPhone: string;
  customerEmail: string;
  jobDescription: string;
}

export interface SignupInput {
  fullName: string;
  email: string;
  phone: string;
  password: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function normalisePhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

// Coerces a field to a string before it is validated. Network input is
// untrusted: TypeScript's types are erased at runtime, so a missing,
// null, or non-string field (a client omitting an optional key, a bad
// JSON body) must not reach `.trim()` or throw. Missing and non-string
// values are treated exactly like an empty string.
function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function required(errors: FieldErrors, key: string, value: string, label: string): boolean {
  if (value.trim() === '') {
    errors[key] = `${label} is required.`;
    return false;
  }
  return true;
}

function maxLength(errors: FieldErrors, key: string, value: string, label: string, max: number): void {
  if (value.trim().length > max) {
    errors[key] = `${label} must be ${max} characters or fewer.`;
  }
}

// The `label` parameter mirrors `required`/`maxLength` for a consistent helper
// shape, but the message text below is fixed by spec and does not use it.
function phoneField(errors: FieldErrors, key: string, value: string, _label: string): void {
  if (normalisePhone(value).length !== 10) {
    errors[key] = 'Enter a 10 digit phone number.';
  }
}

export function validateReferralInput(input: ReferralInput): FieldErrors {
  const errors: FieldErrors = {};

  const customerName = asString(input.customerName);
  const customerAddress = asString(input.customerAddress);
  const customerPhone = asString(input.customerPhone);
  const customerEmail = asString(input.customerEmail);
  const jobDescription = asString(input.jobDescription);

  if (required(errors, 'customerName', customerName, 'Customer name')) {
    maxLength(errors, 'customerName', customerName, 'Customer name', 120);
  }
  if (required(errors, 'customerAddress', customerAddress, 'Address')) {
    maxLength(errors, 'customerAddress', customerAddress, 'Address', 250);
  }
  if (required(errors, 'customerPhone', customerPhone, 'Phone number')) {
    phoneField(errors, 'customerPhone', customerPhone, 'Phone number');
  }
  if (customerEmail.trim() !== '' && !EMAIL_RE.test(customerEmail.trim())) {
    errors.customerEmail = 'Enter a valid email address, or leave it blank.';
  }
  if (required(errors, 'jobDescription', jobDescription, 'Job description')) {
    if (jobDescription.trim().length < 10) {
      errors.jobDescription = 'Give at least a short description of the damage.';
    } else {
      maxLength(errors, 'jobDescription', jobDescription, 'Job description', 4000);
    }
  }

  return errors;
}

export function validateSignupInput(input: SignupInput): FieldErrors {
  const errors: FieldErrors = {};

  const fullName = asString(input.fullName);
  const email = asString(input.email);
  const phone = asString(input.phone);
  const password = asString(input.password);

  if (required(errors, 'fullName', fullName, 'Full name')) {
    maxLength(errors, 'fullName', fullName, 'Full name', 120);
  }
  if (required(errors, 'email', email, 'Email')) {
    if (!EMAIL_RE.test(email.trim())) {
      errors.email = 'Enter a valid email address.';
    }
  }
  if (required(errors, 'phone', phone, 'Phone number')) {
    phoneField(errors, 'phone', phone, 'Phone number');
  }
  if (required(errors, 'password', password, 'Password')) {
    if (password.length < MIN_PASSWORD_LENGTH) {
      errors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    }
  }

  return errors;
}

/**
 * Same shape as SignupInput, for POST /api/users (dispatch creating a
 * dispatch/tech/recon account). Phone is required for the plumber signup
 * flow above but only optional here: dispatch must still be able to create
 * a field tech who hasn't given a phone number yet. When a value IS
 * supplied it is still validated as a 10 digit number.
 */
export function validateInternalUserInput(input: SignupInput): FieldErrors {
  const errors: FieldErrors = {};

  const fullName = asString(input.fullName);
  const email = asString(input.email);
  const phone = asString(input.phone);
  const password = asString(input.password);

  if (required(errors, 'fullName', fullName, 'Full name')) {
    maxLength(errors, 'fullName', fullName, 'Full name', 120);
  }
  if (required(errors, 'email', email, 'Email')) {
    if (!EMAIL_RE.test(email.trim())) {
      errors.email = 'Enter a valid email address.';
    }
  }
  if (phone.trim() !== '') {
    phoneField(errors, 'phone', phone, 'Phone number');
  }
  if (required(errors, 'password', password, 'Password')) {
    if (password.length < MIN_PASSWORD_LENGTH) {
      errors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    }
  }

  return errors;
}
